import { useRef, useEffect, useMemo } from 'react'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { DateOnly, TimeOnly, Formats, legacyJSDateAdapter } from '@sevenrooms/timepiece'
import { useReservationFormState } from '../store'
import { useExperiencesWithAvailability, useSingleDayAvailability } from './useAvailability'
import { useCachedCreateReservationHold } from './useCachedCreateReservationHold'
import { useReservationNavigation } from './useReservationNavigation'
import { useReservationsRoute } from './useReservationsRoute'
import { useVenue } from './useVenue'
import { useWidgetSettings } from './useWidgetSettings'

export function useQueryParamAvailability() {
  // keep in sync with the url in application/modules/core/django/public/views.py
  const { timeslotId, timeslotTime, channel, primaryVenueId } = useReservationsRoute()
  const {
    formState: { selectedTimeSlot, startDate, partySize, startTime, preferredLanguage, trackingSlug, clientId, duration, actual },
    updateFormState,
  } = useReservationFormState()
  const { navigateToFirstStep, currentStep } = useReservationNavigation()
  const formUpdatedRef = useRef(false)
  const formExperiencesUpdatedRef = useRef(false)
  const { id: venueId, urlKey, startOfDayTime } = useVenue()
  const { reservationHoldEnabled } = useWidgetSettings()
  const timeIso = TimeOnly.fromSafe(selectedTimeSlot?.time)?.toHoursMinutesIso()
  const hasQueryParamAvailability =
    !!timeslotTime && !!timeslotId && timeslotId !== selectedTimeSlot?.accessPersistentId && timeslotTime !== timeIso
  const hasSelectedExperience = !!selectedTimeSlot?.experienceId
  const [createReservationHold] = useCachedCreateReservationHold()
  const startDateIso = DateOnly.from(startDate).toIso()
  const haloTimeIntervalMinutes = 120

  const { data: availabilityData } = useSingleDayAvailability({
    venueId,
    venue: urlKey,
    startDate: startDateIso,
    startTime,
    partySize,
    selectedLangCode: preferredLanguage,
    pickedDuration: duration,
    haloTimeIntervalMinutes,
    haloSizeInterval: 5,
    timeSlot: timeslotTime ?? '',
    channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
    startOfDayTime,
    skipRequests: !venueId || !urlKey || !startOfDayTime || !hasQueryParamAvailability || formUpdatedRef.current,
    clientId,
    primaryVenueId,
    actualId: actual?.id,
  })

  const { data: experiences } = useExperiencesWithAvailability({
    venue: venueId,
    partySize,
    startDate: startDateIso,
    startTime,
    haloTimeIntervalMinutes,
    startOfDayTime,
    clientId,
    selectedLangCode: preferredLanguage,
    experienceType: 'PDR',
    skipRequests: !hasSelectedExperience,
  })

  const queryParamAvailability = useMemo(() => {
    if (!availabilityData || !timeslotId || !timeslotTime) {
      return undefined
    }
    return (
      availabilityData.reservationTimes.find(availability => {
        const time = TimeOnly.from(availability.timeIso)
        const timeTwelveHours = legacyJSDateAdapter(time.toJsDate(), Formats.HoursMinutesTwelveHours)
        const timeIso = time.toHoursMinutesIso()
        return availability.accessPersistentId === timeslotId && (timeslotTime === timeIso || timeslotTime === timeTwelveHours)
      }) ?? 'invalid'
    )
  }, [timeslotId, timeslotTime, availabilityData])

  useEffect(() => {
    if (formUpdatedRef.current || !hasQueryParamAvailability || !queryParamAvailability) {
      return
    }
    formUpdatedRef.current = true
    const isNavigationProhibited =
      queryParamAvailability === 'invalid' || (currentStep?.name === 'checkout' && !!queryParamAvailability?.upsellCategories?.length)
    if (isNavigationProhibited) {
      navigateToFirstStep()
      return
    }
    updateFormState({ selectedTimeSlot: queryParamAvailability })
    if (reservationHoldEnabled && venueId) {
      createReservationHold({
        accessPersistentId: queryParamAvailability.accessPersistentId,
        channel: channel || 'SEVENROOMS_WIDGET',
        date: DateOnly.from(queryParamAvailability.timeIso).toIso(),
        partySize,
        shiftPersistentId: queryParamAvailability.shiftPersistentId,
        time: queryParamAvailability.time,
        venueId,
        trackingSlug,
        clientId,
      })
    }
  }, [
    hasQueryParamAvailability,
    createReservationHold,
    navigateToFirstStep,
    queryParamAvailability,
    partySize,
    reservationHoldEnabled,
    trackingSlug,
    updateFormState,
    venueId,
    currentStep?.name,
    clientId,
    channel,
  ])

  useEffect(() => {
    if (formExperiencesUpdatedRef.current || !experiences || !selectedTimeSlot) {
      return
    }
    formExperiencesUpdatedRef.current = true
    const selectedExperience = experiences.find(experience => experience.id === selectedTimeSlot.experienceId)
    if (selectedExperience) {
      updateFormState({ selectedExperience })
    }
  }, [experiences, selectedTimeSlot, updateFormState])

  return {
    hasQueryParamAvailability,
  }
}
